<div class="review-step">
  <!-- Cost of Capital Option -->
  @if (isCostOfCapitalAvailable && hasCreditCardPayments) {
  <div class="cost-of-capital-option">
    <mat-checkbox [(ngModel)]="applyCostOfCapital" (ngModelChange)="onCostOfCapitalToggle()" color="primary">
      Apply Cost of Capital to Credit Card Payments ({{ costOfCapitalPercent }}%)
    </mat-checkbox>
    <p class="option-description">
      <mat-icon>info</mat-icon>
      Cost of capital will be added to credit card payments only. ACH payments are exempt.
    </p>
  </div>
  }

  <!-- Summary cards -->
  <div class="summary-cards">
    <div class="summary-card">
      <div class="card-icon primary">
        <mat-icon>list</mat-icon>
      </div>
      <div class="card-content">
        <div class="card-value">{{ totalRowCount }}</div>
        <div class="card-label">Total Rows</div>
      </div>
    </div>

    <div class="summary-card">
      <div class="card-icon success">
        <mat-icon>check_circle</mat-icon>
      </div>
      <div class="card-content">
        <div class="card-value">{{ readyCount }}</div>
        <div class="card-label">Ready to Process</div>
      </div>
    </div>

    <div class="summary-card">
      <div class="card-icon error">
        <mat-icon>person_off</mat-icon>
      </div>
      <div class="card-content">
        <div class="card-value">{{ customerIssueCount }}</div>
        <div class="card-label">Customer Issues</div>
      </div>
    </div>

    <div class="summary-card">
      <div class="card-icon warning">
        <mat-icon>calculate</mat-icon>
      </div>
      <div class="card-content">
        <div class="card-value">{{ invalidAmountCount }}</div>
        <div class="card-label">Invalid Amount</div>
      </div>
    </div>

    <div class="summary-card">
      <div class="card-icon warning">
        <mat-icon>receipt_long</mat-icon>
      </div>
      <div class="card-content">
        <div class="card-value">{{ missingInvoiceCount }}</div>
        <div class="card-label">Missing Invoice</div>
      </div>
    </div>

    <div class="summary-card">
      <div class="card-icon primary">
        <mat-icon>attach_money</mat-icon>
      </div>
      <div class="card-content">
        <div class="card-value">{{ totalAmount | currency }}</div>
        <div class="card-label">Total Amount</div>
      </div>
    </div>

    @if (applyCostOfCapital && totalCostOfCapital > 0) {
    <div class="summary-card">
      <div class="card-icon warning">
        <mat-icon>account_balance</mat-icon>
      </div>
      <div class="card-content">
        <div class="card-value">{{ totalCostOfCapital | currency }}</div>
        <div class="card-label">Cost of Capital</div>
      </div>
    </div>

    <div class="summary-card grand-total">
      <div class="card-icon">
        <mat-icon>payments</mat-icon>
      </div>
      <div class="card-content">
        <div class="card-value">{{ grandTotal | currency }}</div>
        <div class="card-label">Grand Total</div>
      </div>
    </div>
    }
  </div>

  <!-- Ready to process table -->
  @if (readyPayments.length > 0) {
  <div class="payments-section">
    <h4>Payments Ready to Process ({{ readyCount }})</h4>
    <div class="table-container">
      <table mat-table [dataSource]="readyPayments" class="payments-table">
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let match">
            <mat-icon [class]="getStatusClass(match.status)" [matTooltip]="match.error || 'Ready to process'">
              {{ getStatusIcon(match.status) }}
            </mat-icon>
          </td>
        </ng-container>

        <ng-container matColumnDef="accountNumber">
          <th mat-header-cell *matHeaderCellDef>Account Number</th>
          <td mat-cell *matCellDef="let match">{{ match.row.accountNumber }}</td>
        </ng-container>

        <ng-container matColumnDef="customerName">
          <th mat-header-cell *matHeaderCellDef>Customer Name</th>
          <td mat-cell *matCellDef="let match">
            {{ match.customer?.displayName || match.customer?.companyName || '-' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="amount">
          <th mat-header-cell *matHeaderCellDef>Amount</th>
          <td mat-cell *matCellDef="let match" class="amount-cell">
            {{ match.row.amount | currency }}
          </td>
        </ng-container>

        <ng-container matColumnDef="costOfCapital">
          <th mat-header-cell *matHeaderCellDef>Cost of Capital</th>
          <td mat-cell *matCellDef="let match" class="amount-cell">
            <span *ngIf="getCostOfCapitalForMatch(match) > 0">
              {{ getCostOfCapitalForMatch(match) | currency }}
            </span>
            <span *ngIf="getCostOfCapitalForMatch(match) === 0" class="exempt-text">
              {{ match.paymentMethod?.accountType === 'ACH' ? 'N/A (ACH)' : '-' }}
            </span>
          </td>
        </ng-container>

        <ng-container matColumnDef="invoiceNumber">
          <th mat-header-cell *matHeaderCellDef>Invoice #</th>
          <td mat-cell *matCellDef="let match">{{ match.row.invoiceNumber || '-' }}</td>
        </ng-container>

        <ng-container matColumnDef="paymentMethod">
          <th mat-header-cell *matHeaderCellDef>Payment Method</th>
          <td mat-cell *matCellDef="let match">
            <span class="payment-method" [matTooltip]="match.paymentMethod?.isDefault ? 'Default payment method' : ''">
              {{ getPaymentMethodDisplay(match) }}
              @if (match.paymentMethod?.isDefault) {
              <mat-icon class="default-icon">star</mat-icon>
              }
            </span>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="getDisplayedColumnsForMatched()"></tr>
        <tr mat-row *matRowDef="let row; columns: getDisplayedColumnsForMatched()"></tr>
      </table>
    </div>
  </div>
  }

  <!-- Issue rows table: customer issues + invalid amounts -->
  @if (issuePayments.length > 0) {
  <div class="payments-section error-section">
    <h4>Needs Attention ({{ issuePayments.length }})</h4>
    <div class="table-container">
      <table mat-table [dataSource]="issuePayments" class="payments-table">
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let match">
            <div class="status-indicators">
              <mat-icon [class]="getStatusClass(match.status)" [matTooltip]="match.error || ''">
                {{ getStatusIcon(match.status) }}
              </mat-icon>
              @if (!match.row.isAmountValid) {
              <mat-icon class="status-warning amount-invalid-badge" matTooltip="Invalid amount — please edit">
                calculate
              </mat-icon>
              } @if (!match.row.isInvoiceValid) {
              <mat-icon class="status-warning invoice-invalid-badge" matTooltip="Missing invoice number — please edit">
                receipt_long
              </mat-icon>
              }
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="accountNumber">
          <th mat-header-cell *matHeaderCellDef>Account Number</th>
          <td mat-cell *matCellDef="let match">
            {{ match.row.accountNumber || '—' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="customerName">
          <th mat-header-cell *matHeaderCellDef>Customer Name</th>
          <td mat-cell *matCellDef="let match">
            {{ match.customer?.displayName || match.customer?.companyName || '-' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="amount">
          <th mat-header-cell *matHeaderCellDef>Amount</th>
          <td
            mat-cell
            *matCellDef="let match"
            class="amount-cell"
            [class.amount-cell-invalid]="!match.row.isAmountValid"
          >
            @if (match.row.isAmountValid) {
            {{ match.row.amount | currency }}
            } @else {
            <mat-form-field appearance="outline" class="inline-amount-edit" subscriptSizing="dynamic">
              <input
                matInput
                type="text"
                [value]="match.row.rawAmount"
                (blur)="onAmountBlur(match, $any($event.target).value)"
                (keydown.enter)="onAmountBlur(match, $any($event.target).value)"
                placeholder="e.g. 150.00"
              />
              <mat-icon matSuffix color="warn" matTooltip="Invalid amount">error</mat-icon>
            </mat-form-field>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="invoiceNumber">
          <th mat-header-cell *matHeaderCellDef>Invoice #</th>
          <td
            mat-cell
            *matCellDef="let match"
            class="invoice-cell"
            [class.invoice-cell-invalid]="!match.row.isInvoiceValid"
          >
            @if (match.row.isInvoiceValid) {
            {{ match.row.invoiceNumber }}
            } @else {
            <mat-form-field
              appearance="outline"
              class="inline-invoice-edit"
              subscriptSizing="dynamic"
              matTooltip="Invoice number is required"
            >
              <input
                matInput
                type="text"
                [value]="match.row.rawInvoiceNumber"
                (blur)="onInvoiceBlur(match, $any($event.target).value)"
                (keydown.enter)="onInvoiceBlur(match, $any($event.target).value)"
                placeholder="Enter invoice #"
              />
            </mat-form-field>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="error">
          <th mat-header-cell *matHeaderCellDef>Issue</th>
          <td
            mat-cell
            *matCellDef="let match"
            class="error-cell"
            [matTooltip]="getIssueText(match)"
            matTooltipPosition="above"
            matTooltipShowDelay="150"
          >
            {{ getIssueText(match) }}
          </td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef>Actions</th>
          <td mat-cell *matCellDef="let match" class="actions-cell">
            @if (match.status === 'no-customer') {
            <div class="action-buttons">
              <button mat-button color="primary" (click)="onAddCustomer(match)" matTooltip="Create new customer">
                <mat-icon>person_add</mat-icon>
                Add Customer
              </button>
              <button mat-button color="accent" (click)="onSelectCustomer(match)" matTooltip="Select existing customer">
                <mat-icon>search</mat-icon>
                Select Customer
              </button>
            </div>
            } @if (match.status === 'no-payment-method') {
            <div class="action-buttons">
              <button mat-button color="accent" (click)="onAddCard(match)" matTooltip="Add a new credit card">
                <mat-icon>add_card</mat-icon>
                Add Card
              </button>
              <button
                mat-button
                color="primary"
                (click)="onSelectPaymentMethod(match)"
                [disabled]="!hasPaymentMethods(match)"
                matTooltip="Select payment method"
              >
                <mat-icon>credit_card</mat-icon>
                Select Payment Method
              </button>
            </div>
            }
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="getDisplayedColumnsForIssues()"></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: getDisplayedColumnsForIssues()"
          class="error-row"
          [class.invalid-amount-row]="!row.row.isAmountValid"
          [class.missing-invoice-row]="!row.row.isInvoiceValid"
        ></tr>
      </table>
    </div>
    <div class="error-note">
      <mat-icon>info</mat-icon>
      <span
        >Fix invalid amounts and missing invoice numbers by typing a valid value and pressing Enter or clicking away.
        Use the action buttons to resolve customer issues. Rows with a missing invoice cannot be processed because the
        server requires a non-blank reference for every sale.</span
      >
    </div>
  </div>
  } @if (matches.length === 0) {
  <div class="empty-state">
    <mat-icon>inbox</mat-icon>
    <p>No payments to review</p>
  </div>
  }
</div>
